import { AEMLink } from '@msc-b2c/interfaces/types';
import { mscSet, RootState } from '@msc-b2c/shared-utils';
import { useRef, useState } from 'react';
import {
  getDEMRedirectURL,
  retrieveDirectLoginQueryParams,
  retrieveDirectLoginType,
} from './directLoginUtility';
import { GetBookingMyMscResponse, LightLoginRequestForm } from '@msc-b2c/interfaces/interfaces';
import { useSelector } from 'react-redux';

export enum DIRECT_LOGIN_TYPE {
  NONE,
  BF,
  DEM,
}

export interface DirectLoginParams {
  name: string;
  surname: string;
  bookingNumber: string;
  bf: string;
  serviceType: string;
  itemCode: string;
  signature: string;
  signatureDate: string;
  cruiseId: string;
  dob: string;
}

export default function useDirectLogin(demFallbackLink: AEMLink, prepaidPagesMappedURLs: []) {
  const bookingFunnelConfirmationConfig = useSelector(
    (state: RootState) => state.genericConfigs.configs.bookingFunnelConfirmationConfig
  );

  const { enableIdentityPopup, redirects } =
    bookingFunnelConfirmationConfig?.bookingFunnelConfirmation ?? {};

  const queryParams = retrieveDirectLoginQueryParams();
  const lightLoginParams: LightLoginRequestForm = {
    ...queryParams,
    customer: { dateOfBirth: queryParams.dob },
  };

  const type = retrieveDirectLoginType(queryParams);

  const [directLoginType, setDirectLoginType] = useState(type);

  const [isConfirmationPopup, setIsConfirmationPopup] = useState(
    directLoginType === DIRECT_LOGIN_TYPE.DEM ||
      (enableIdentityPopup && directLoginType === DIRECT_LOGIN_TYPE.BF)
  );

  const isDefaultRedirect = useRef(true);

  const [isRecaptchaDisabled, setIsRecaptchaDisabled] = useState(
    directLoginType === DIRECT_LOGIN_TYPE.DEM
  );

  const [blankFormOnError, setBlankFormOnError] = useState(
    directLoginType === DIRECT_LOGIN_TYPE.DEM
  );

  const getRedirectURL = (booking?: GetBookingMyMscResponse) => {
    if (directLoginType === DIRECT_LOGIN_TYPE.NONE) return undefined;

    const invalidateRedirect = () => {
      isDefaultRedirect.current = true;

      return undefined;
    };

    if (directLoginType === DIRECT_LOGIN_TYPE.DEM) {
      const demRedirectURL = getDEMRedirectURL(
        demFallbackLink,
        prepaidPagesMappedURLs,
        queryParams.serviceType
      );
      if (demRedirectURL) {
        isDefaultRedirect.current = false;
        return demRedirectURL;
      } else return invalidateRedirect();
    }

    if (!booking) {
      isDefaultRedirect.current = false;

      return undefined;
    }

    const remainingBalance = booking.total?.due?.gross;
    if (!remainingBalance || Number(remainingBalance) === 0) return invalidateRedirect();

    const paymentRedirect = redirects?.lightLoginPaymentPageLink;
    if (!!paymentRedirect?.valid && !!paymentRedirect?.url) {
      isDefaultRedirect.current = false;

      return paymentRedirect.url;
    }

    return invalidateRedirect();
  };

  const getFallbackURL = () => {
    if (directLoginType === DIRECT_LOGIN_TYPE.NONE) return undefined;

    if (directLoginType === DIRECT_LOGIN_TYPE.DEM) {
      return (!!demFallbackLink?.valid && demFallbackLink.url) || undefined;
    }

    return undefined;
  };

  const onConfirmationPopupSubmit = () => {
    if (directLoginType === DIRECT_LOGIN_TYPE.DEM) {
      const isDEMLoginValid = !!getRedirectURL() || !!queryParams.itemCode;
      if (isDEMLoginValid) {
        mscSet(
          'session',
          'demLogin',
          { type: queryParams.serviceType, code: queryParams.itemCode } as {
            type: string;
            code: string;
          },
          365
        );
      }
    }
  };

  const onLoginError = () => {
    if (directLoginType !== DIRECT_LOGIN_TYPE.NONE) {
      setIsConfirmationPopup(false);
    }
  };

  const invalidateDirectLogin = () => {
    setDirectLoginType(DIRECT_LOGIN_TYPE.NONE);
    setIsConfirmationPopup(false);
    setIsRecaptchaDisabled(false);
    setBlankFormOnError(false);
  };

  return {
    directLoginType,
    isConfirmationPopup,
    isRecaptchaDisabled,
    lightLoginParams,
    blankFormOnError,
    isDefaultRedirect,
    getRedirectURL,
    getFallbackURL,
    onConfirmationPopupSubmit,
    onLoginError,
    invalidateDirectLogin,
  };
}
